import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, CircleDashed, Lock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { useCampaignFacebookStatus } from '@/api/campaigns/use-campaign-facebook';
import { useIntegrations } from '@/api/integrations/use-integrations';
import type { TenantIntegration } from '@/api/integrations/integrationService';
import {
    INTEGRATION_CATALOG,
    INTEGRATION_GROUPS,
    channelsInGroup,
    summaryFor,
    type CatalogChannel,
    type IntegrationChannel,
} from '@/components/integrations/catalog';
import {
    countStates,
    deriveChannelStatus,
    deriveFacebookStatus,
    derivePaymentStatus,
    summarizePayments,
    type ChannelStatus,
} from '@/components/integrations/integrationHelpers';
import { IntegrationCard } from '@/components/integrations/IntegrationCard';
import { ServiceDrawer } from '@/components/integrations/ServiceDrawer';
import { FacebookDrawer } from '@/components/integrations/FacebookDrawer';
import { PaymentsDrawer } from '@/components/integrations/PaymentsDrawer';
import { useMetaLine } from '@/components/integrations/useMetaLine';

const isChannel = (value: string | null): value is IntegrationChannel =>
    !!value && INTEGRATION_CATALOG.some(channel => channel.id === value);

/**
 * Integrations: grouped service cards (one status, one detail, one meta line, one button)
 * that open a drawer per service. Replaces the old IntegrationManagementPage.
 */
export const IntegrationsPage: React.FC = () => {
    const { t } = useTranslation('crm');
    const { hasPermission } = usePermissions();
    const describe = useMetaLine();
    const [searchParams, setSearchParams] = useSearchParams();

    const canView = hasPermission(ModulePermission.INTEGRATIONS, PermissionAction.VIEW);
    const canEdit = hasPermission(ModulePermission.INTEGRATIONS, PermissionAction.EDIT);
    const canViewCampaigns = hasPermission(ModulePermission.CAMPAIGNS, PermissionAction.VIEW);
    const canSync =
        hasPermission(ModulePermission.CAMPAIGNS, PermissionAction.EDIT) ||
        hasPermission(ModulePermission.CAMPAIGNS, PermissionAction.CREATE);

    const integrationsQuery = useIntegrations({ enabled: canView });
    const facebookQuery = useCampaignFacebookStatus({ enabled: canView && canViewCampaigns });

    // `?service=email` opens that drawer (deep link from other pages).
    const requested = searchParams.get('service');
    const [openChannel, setOpenChannel] = useState<IntegrationChannel | null>(
        isChannel(requested) ? requested : null
    );
    const [openCount, setOpenCount] = useState(0);
    const [isOpen, setIsOpen] = useState(isChannel(requested));

    const open = (channel: IntegrationChannel) => {
        setOpenChannel(channel);
        setOpenCount(count => count + 1);
        setIsOpen(true);
    };

    const onOpenChange = (next: boolean) => {
        setIsOpen(next);
        if (!next && searchParams.has('service')) {
            const params = new URLSearchParams(searchParams);
            params.delete('service');
            setSearchParams(params, { replace: true });
        }
    };

    const rows = useMemo<TenantIntegration[]>(
        () => integrationsQuery.data ?? [],
        [integrationsQuery.data]
    );
    const rowsFor = (channel: IntegrationChannel) =>
        rows.filter(row => row.integration_type === channel);
    const facebookStatus = facebookQuery.data?.data;

    const statusOf = (channel: CatalogChannel): ChannelStatus => {
        const channelRows = rowsFor(channel.id);
        if (channel.id === 'facebook') {
            return deriveFacebookStatus(facebookStatus, channelRows[0]);
        }
        if (channel.id === 'payment') return derivePaymentStatus(channelRows);
        return deriveChannelStatus(channelRows);
    };

    const states = INTEGRATION_CATALOG.map(channel => statusOf(channel).state);
    const counts = countStates(states);
    const isLoading = canView && integrationsQuery.isLoading;

    const cardCopy = (channel: CatalogChannel, status: ChannelStatus) => {
        const channelRows = rowsFor(channel.id);
        let detail: string;
        let meta = describe(status);

        if (status.reason === 'none' || status.reason === 'not_configured') {
            detail = t('integrations.card.setUpPrompt');
        } else if (channel.id === 'payment') {
            const summary = summarizePayments(channelRows);
            detail =
                summary.live + summary.sandbox > 0
                    ? t('integrations.payments.counts', {
                          live: summary.live,
                          sandbox: summary.sandbox,
                      })
                    : t('integrations.payments.noneEnabled');
            if (summary.failing.length > 0) {
                meta = {
                    text: summary.failing
                        .map(row => {
                            const name = summaryFor(row).split(' · ')[0];
                            return `${name}: ${row.last_check_message || t('integrations.meta.checkFailed')}`;
                        })
                        .join(' · '),
                    tone: 'error',
                };
            }
        } else if (status.row) {
            detail = summaryFor(status.row);
        } else {
            detail = t('integrations.card.setUpPrompt');
        }
        return { detail, meta };
    };

    const renderCard = (channel: CatalogChannel) => {
        const status = statusOf(channel);
        const { detail, meta } = cardCopy(channel, status);
        return (
            <IntegrationCard
                key={channel.id}
                channel={channel}
                state={status.state}
                detail={detail}
                meta={meta.text}
                metaTone={meta.tone}
                canManage={canEdit}
                onOpen={() => open(channel.id)}
            />
        );
    };

    const activeRows = openChannel ? rowsFor(openChannel) : [];

    return (
        <PageContainer>
            <PageHeader
                title={t('integrations.title')}
                description={t('integrations.description')}
                actions={
                    canView ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                void integrationsQuery.refetch();
                                void facebookQuery.refetch();
                            }}
                            disabled={integrationsQuery.isFetching}
                        >
                            <RefreshCw
                                className={
                                    integrationsQuery.isFetching
                                        ? 'h-4 w-4 animate-spin'
                                        : 'h-4 w-4'
                                }
                            />
                            {t('actions.refresh')}
                        </Button>
                    ) : undefined
                }
            />

            {!canView ? (
                <EmptyState
                    icon={Lock}
                    title={t('integrations.noAccess.title')}
                    description={t('integrations.noAccess.description')}
                />
            ) : integrationsQuery.isError ? (
                <EmptyState
                    icon={AlertTriangle}
                    title={t('integrations.error.title')}
                    description={t('integrations.error.description')}
                    action={
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => integrationsQuery.refetch()}
                        >
                            {t('actions.refresh')}
                        </Button>
                    }
                />
            ) : (
                <>
                    <StatTileGrid>
                        <StatTile
                            label={t('integrations.status.connected')}
                            value={counts.connected}
                            icon={CheckCircle2}
                            tone="success"
                            isLoading={isLoading}
                        />
                        <StatTile
                            label={t('integrations.status.needs_attention')}
                            value={counts.needs_attention}
                            icon={AlertTriangle}
                            tone="warning"
                            isLoading={isLoading}
                        />
                        <StatTile
                            label={t('integrations.status.not_set_up')}
                            value={counts.not_set_up}
                            icon={CircleDashed}
                            tone="neutral"
                            isLoading={isLoading}
                        />
                    </StatTileGrid>

                    {INTEGRATION_GROUPS.map(group => (
                        <section
                            key={group.id}
                            aria-labelledby={`group-${group.id}`}
                            className="space-y-3"
                        >
                            <h2
                                id={`group-${group.id}`}
                                className="text-sm font-semibold text-muted-foreground"
                            >
                                {t(group.label)}
                            </h2>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                                {isLoading
                                    ? channelsInGroup(group.id).map(channel => (
                                          <Skeleton key={channel.id} className="h-44 rounded-xl" />
                                      ))
                                    : channelsInGroup(group.id).map(renderCard)}
                            </div>
                        </section>
                    ))}
                </>
            )}

            {openChannel &&
                (openChannel === 'facebook' ? (
                    <FacebookDrawer
                        key={openCount}
                        open={isOpen}
                        onOpenChange={onOpenChange}
                        row={activeRows[0]}
                        status={facebookStatus}
                        canEdit={canEdit}
                        canCheck={canView}
                        canSync={canSync}
                    />
                ) : openChannel === 'payment' ? (
                    <PaymentsDrawer
                        key={openCount}
                        open={isOpen}
                        onOpenChange={onOpenChange}
                        rows={activeRows}
                        canEdit={canEdit}
                        canCheck={canView}
                    />
                ) : (
                    <ServiceDrawer
                        key={openCount}
                        open={isOpen}
                        onOpenChange={onOpenChange}
                        channelId={openChannel}
                        rows={activeRows}
                        canEdit={canEdit}
                        canCheck={canView}
                    />
                ))}
        </PageContainer>
    );
};

export default IntegrationsPage;
